/**
 * Product Hub — promotions.
 *
 * A promotion is a **layer over the price, never a write into it** (ADR-004). Nothing in this file
 * produces a new `StorePrice`; it computes what comes off a cart at evaluation time, so a promotion
 * that ends simply stops matching and there is no "revert" step that can fail halfway.
 *
 * This file is the **vocabulary and the lifecycle**: the eight types, the condition and reward shapes,
 * the scopes, the currency rule, and the derived state. The arithmetic — what actually comes off a
 * cart, and which of several promotions wins — is `productHub.promotionEngine.ts`, split for the same
 * reason `productHub.shipping.ts` and `productHub.fulfillment.ts` are two files: one describes the
 * shape of a decision, the other makes it, and together they would be five hundred lines.
 *
 * The money rule the spec is emphatic about (§39, §44): a **percentage** promotion may span stores in
 * different currencies, while anything with a fixed monetary amount needs a currency and all its
 * stores must share it. There is no conversion anywhere in this module.
 *
 * No React, no MUI, no browser globals — the backend imports this file directly through
 * `@malash-office/types/product-hub/promotions`.
 */
import { PhOption, phOptions } from './productHub.core';

/* ------------------------------------------------------------------ */
/* Vocabulary                                                          */
/* ------------------------------------------------------------------ */

/** The eight kinds of doc 2 §36. */
export const PROMOTION_TYPES = [
  'percentage',
  'fixed_discount',
  'fixed_price',
  'quantity',
  'buy_x_get_y',
  'second_item',
  'cart',
  'free_shipping'
] as const;
export type PromotionType = (typeof PROMOTION_TYPES)[number];

export const PROMOTION_TYPE_LABELS: Record<PromotionType, string> = {
  percentage: 'הנחה באחוזים',
  fixed_discount: 'הנחה בסכום קבוע',
  fixed_price: 'מחיר מבצע',
  quantity: 'מבצע כמות',
  buy_x_get_y: 'קנה X קבל Y',
  second_item: 'הנחה לפריט השני',
  cart: 'מבצע על סל הקניות',
  free_shipping: 'משלוח חינם'
};

/** The one-line example each type carries in the wizard's first step. */
export const PROMOTION_TYPE_HINTS: Record<PromotionType, string> = {
  percentage: 'לדוגמה: 20% הנחה על מוצר, קטגוריה או כל הסל',
  fixed_discount: 'לדוגמה: 50 ₪ הנחה',
  fixed_price: 'לדוגמה: במקום 299 ₪ — 249 ₪',
  quantity: 'לדוגמה: קנה 3 וקבל 20% הנחה',
  buy_x_get_y: 'לדוגמה: 1+1, או קנה 2 קבל 1',
  second_item: 'לדוגמה: הפריט השני ב-50% הנחה',
  cart: 'לדוגמה: 10% הנחה על כל הסל',
  free_shipping: 'לדוגמה: משלוח חינם מעל 250 ₪'
};

export const PROMOTION_TYPE_OPTIONS: PhOption<PromotionType>[] = phOptions(PROMOTION_TYPES, PROMOTION_TYPE_LABELS);

/**
 * The lifecycle.
 *
 * `scheduled` and `ended` are **derived** from the dates rather than stored — see
 * `resolvePromotionState`. What is stored is the operator's intent: a draft, a live promotion, or one
 * they paused. Storing "ended" too would mean a cron job to flip it and a window where the database
 * disagrees with the clock.
 */
export const PROMOTION_STATUSES = ['draft', 'active', 'paused'] as const;
export type PromotionStatus = (typeof PROMOTION_STATUSES)[number];

export const PROMOTION_STATUS_LABELS: Record<PromotionStatus, string> = {
  draft: 'טיוטה',
  active: 'פעיל',
  paused: 'מושהה'
};

export const PROMOTION_STATUS_OPTIONS = phOptions(PROMOTION_STATUSES, PROMOTION_STATUS_LABELS);

/** What the screen shows, once the clock is taken into account. */
export const PROMOTION_STATES = ['draft', 'scheduled', 'active', 'paused', 'ended'] as const;
export type PromotionState = (typeof PROMOTION_STATES)[number];

export const PROMOTION_STATE_LABELS: Record<PromotionState, string> = {
  draft: 'טיוטה',
  scheduled: 'מתוזמן',
  active: 'פעיל',
  paused: 'מושהה',
  ended: 'הסתיים'
};

export const PROMOTION_STATE_OPTIONS = phOptions(PROMOTION_STATES, PROMOTION_STATE_LABELS);

/** Automatic, or unlocked by a coupon code. */
export const ACTIVATION_TYPES = ['automatic', 'coupon'] as const;
export type ActivationType = (typeof ACTIVATION_TYPES)[number];

export const ACTIVATION_TYPE_LABELS: Record<ActivationType, string> = {
  automatic: 'אוטומטי',
  coupon: 'קוד קופון'
};

export const ACTIVATION_TYPE_OPTIONS = phOptions(ACTIVATION_TYPES, ACTIVATION_TYPE_LABELS);

/** One condition per promotion in the MVP (§37). */
export const CONDITION_TYPES = ['none', 'min_amount', 'min_quantity', 'required_product'] as const;
export type ConditionType = (typeof CONDITION_TYPES)[number];

export const CONDITION_TYPE_LABELS: Record<ConditionType, string> = {
  none: 'ללא תנאי נוסף',
  min_amount: 'סכום קנייה מינימלי',
  min_quantity: 'מספר פריטים מינימלי',
  required_product: 'מוצר מסוים בסל'
};

export const CONDITION_TYPE_OPTIONS = phOptions(CONDITION_TYPES, CONDITION_TYPE_LABELS);

/** One reward per promotion in the MVP (§37). */
export const REWARD_TYPES = [
  'percentage',
  'fixed_amount',
  'fixed_price',
  'free_item',
  'discounted_item',
  'free_shipping'
] as const;
export type RewardType = (typeof REWARD_TYPES)[number];

export const REWARD_TYPE_LABELS: Record<RewardType, string> = {
  percentage: 'אחוז הנחה',
  fixed_amount: 'סכום הנחה',
  fixed_price: 'מחיר קבוע',
  free_item: 'פריט חינם',
  discounted_item: 'פריט נוסף בהנחה',
  free_shipping: 'משלוח חינם'
};

export const REWARD_TYPE_OPTIONS = phOptions(REWARD_TYPES, REWARD_TYPE_LABELS);

/** What the promotion applies to (§37's Targets). */
export const PRODUCT_SCOPES = ['all', 'categories', 'products'] as const;
export type ProductScope = (typeof PRODUCT_SCOPES)[number];

export const PRODUCT_SCOPE_LABELS: Record<ProductScope, string> = {
  all: 'כל המוצרים',
  categories: 'קטגוריות נבחרות',
  products: 'מוצרים נבחרים'
};

export const PRODUCT_SCOPE_OPTIONS = phOptions(PRODUCT_SCOPES, PRODUCT_SCOPE_LABELS);

/**
 * Which stores the promotion runs in (§37's Scope, §38).
 *
 * `product_stores` is the interesting one: "every store that carries these products", evaluated **at
 * evaluation time** rather than expanded into rows on save. That is what makes
 * `autoIncludeFutureStores` self-healing, and it is also why `PromotionStores` is not the whole truth
 * about where such a promotion applies.
 */
export const STORE_SCOPES = ['selected', 'all', 'product_stores'] as const;
export type StoreScope = (typeof STORE_SCOPES)[number];

export const STORE_SCOPE_LABELS: Record<StoreScope, string> = {
  selected: 'חנויות נבחרות',
  all: 'כל החנויות',
  product_stores: 'כל החנויות שמוכרות את המוצרים'
};

export const STORE_SCOPE_OPTIONS = phOptions(STORE_SCOPES, STORE_SCOPE_LABELS);

/** The role a linked product plays (§37 merged into one table by the plan). */
export const PROMOTION_PRODUCT_ROLES = ['target', 'required', 'reward'] as const;
export type PromotionProductRole = (typeof PROMOTION_PRODUCT_ROLES)[number];

export const PROMOTION_PRODUCT_ROLE_LABELS: Record<PromotionProductRole, string> = {
  target: 'המוצר שההנחה חלה עליו',
  required: 'מוצר שנדרש בסל',
  reward: 'המוצר שמתקבל כהטבה'
};

/* ------------------------------------------------------------------ */
/* Currency                                                            */
/* ------------------------------------------------------------------ */

/**
 * Does this promotion carry a fixed monetary amount? (§39, §44, ADR-016)
 *
 * A percentage travels across currencies untouched — 20% is 20% in shekels and in dollars. A fixed
 * amount does not, and there is no conversion in the MVP, so such a promotion needs a currency and
 * every participating store must share it. The check is one function because three places ask it: the
 * write guard, the store picker, and the wizard's summary.
 */
export const requiresCurrency = (input: {
  type: PromotionType;
  rewardType: RewardType;
  conditionType: ConditionType;
}): boolean =>
  ['fixed_discount', 'fixed_price'].includes(input.type) ||
  ['fixed_amount', 'fixed_price'].includes(input.rewardType) ||
  input.conditionType === 'min_amount';

/* ------------------------------------------------------------------ */
/* State                                                               */
/* ------------------------------------------------------------------ */

/** The stored intent plus the window, as the resolver needs them. */
export interface PromotionWindow {
  status: PromotionStatus;
  startAt: string | Date | null;
  endAt: string | Date | null;
}

/**
 * What the promotion *is*, right now.
 *
 * Derived rather than stored: the alternative is a scheduled job flipping `status` to `ended`, and a
 * window — however short — in which the database says a promotion is live and the clock says it is
 * over. A paused promotion stays paused whatever the dates say, because that is a human's decision
 * overriding the schedule.
 */
export const resolvePromotionState = (promotion: PromotionWindow, now: Date = new Date()): PromotionState => {
  if (promotion.status === 'draft') return 'draft';
  if (promotion.status === 'paused') return 'paused';

  const start = promotion.startAt ? new Date(promotion.startAt) : null;
  const end = promotion.endAt ? new Date(promotion.endAt) : null;

  if (end && end.getTime() <= now.getTime()) return 'ended';
  if (start && start.getTime() > now.getTime()) return 'scheduled';
  return 'active';
};

/** Only a live promotion may take money off a cart. */
export const isPromotionRunning = (promotion: PromotionWindow, now: Date = new Date()): boolean =>
  resolvePromotionState(promotion, now) === 'active';

/* ------------------------------------------------------------------ */
/* Warnings                                                            */
/* ------------------------------------------------------------------ */

/** What the wizard's summary and the promotion's card check before somebody hits "activate". */
export interface PromotionDraftShape {
  type: PromotionType;
  status: PromotionStatus;
  activationType: ActivationType;
  couponCode: string | null;
  currency: string | null;
  productScope: ProductScope;
  storeScope: StoreScope;
  conditionType: ConditionType;
  rewardType: RewardType;
  percentage: number | null;
  fixedAmount: number | null;
  fixedPrice: number | null;
  minimumAmount: number | null;
  minimumQuantity: number | null;
  startAt: string | Date | null;
  endAt: string | Date | null;
  /** Counts, so this stays a pure function over what the screen already loaded. */
  storeCount: number;
  targetCount: number;
  /** How many of the chosen stores are in a different currency than the promotion. */
  mismatchedStoreCount?: number;
}

/**
 * Everything wrong with a promotion, in Hebrew, without refusing to save it.
 *
 * A promotion is built over several steps and saved as a draft in between, so most of these are normal
 * mid-flight states rather than errors. The two the **server** does refuse are the currency ones (§39),
 * because a fixed amount with no currency has no meaning at all and a mismatched store would silently
 * never match; everything else is a warning the operator sees before activating.
 */
export const promotionWarnings = (draft: PromotionDraftShape): string[] => {
  const warnings: string[] = [];
  const needsCurrency = requiresCurrency(draft);

  if (needsCurrency && !draft.currency) warnings.push('מבצע עם סכום כספי קבוע חייב מטבע');
  if (needsCurrency && draft.mismatchedStoreCount) {
    warnings.push(`${draft.mismatchedStoreCount} מהחנויות שנבחרו במטבע אחר — המבצע לא יחול בהן`);
  }

  if (draft.storeScope === 'selected' && draft.storeCount === 0) warnings.push('המבצע אינו משויך לאף חנות');
  if (draft.productScope !== 'all' && draft.targetCount === 0) warnings.push('לא נבחרו מוצרים או קטגוריות');

  if (draft.activationType === 'coupon' && !draft.couponCode?.trim()) warnings.push('נבחרה הפעלה בקופון ללא קוד');

  if (draft.rewardType === 'percentage' && !draft.percentage) warnings.push('לא הוגדר אחוז הנחה');
  if (draft.rewardType === 'fixed_amount' && !draft.fixedAmount) warnings.push('לא הוגדר סכום הנחה');
  if (draft.rewardType === 'fixed_price' && !draft.fixedPrice) warnings.push('לא הוגדר מחיר מבצע');
  if (draft.conditionType === 'min_amount' && !draft.minimumAmount) warnings.push('לא הוגדר סכום קנייה מינימלי');
  if (draft.conditionType === 'min_quantity' && !draft.minimumQuantity) warnings.push('לא הוגדר מספר פריטים מינימלי');

  // A window that has already closed is worth saying out loud: the promotion would be created and
  // never run, which looks identical to "saved successfully" from the operator's side.
  if (draft.startAt && draft.endAt && new Date(draft.endAt).getTime() <= new Date(draft.startAt).getTime()) {
    warnings.push('תאריך הסיום אינו אחרי תאריך ההתחלה');
  }

  return warnings;
};
